import { INGXLoggerMetadata, INGXLoggerMonitor, NgxLoggerLevel } from 'ngx-logger';

import { getFaro, pushErrorToFaro, pushLogToFaro } from './faro';

// LogLevel string values from @grafana/faro-web-sdk — avoids a top-level SDK import
// that would pull the full OpenTelemetry stack into the initial bundle.
const NGX_TO_FARO_LEVEL: Partial<Record<NgxLoggerLevel, string>> = {
  [NgxLoggerLevel.INFO]: 'info',
  [NgxLoggerLevel.LOG]: 'info',
  [NgxLoggerLevel.WARN]: 'warn',
  [NgxLoggerLevel.ERROR]: 'error',
  [NgxLoggerLevel.FATAL]: 'error',
};

/**
 * NGXLogger monitor that forwards log events to Grafana Faro with correct log levels.
 *
 * ngx-logger's console output (captured via Faro's captureConsole instrumentation) loses the
 * severity because the CSS-styled format strings emitted by logModern make level detection
 * unreliable. This monitor intercepts log events before they reach the console and pushes them
 * directly to the Faro API:
 *  - ERROR / FATAL → faro.api.pushError()  (Faro Exception, preserves stack trace)
 *  - all other levels → faro.api.pushLog() with the mapped LogLevel
 *
 * Register it once via NGXLogger.registerMonitor() at app bootstrap (see initLogger).
 */
export class FaroLoggerMonitor implements INGXLoggerMonitor {
  onLog(logObject: INGXLoggerMetadata): void {
    if (!getFaro()) {
      return;
    }

    const { level, message, additional = [] } = logObject;

    if (level === NgxLoggerLevel.ERROR || level === NgxLoggerLevel.FATAL) {
      pushErrorToFaro(this.extractError(message, additional));
    } else {
      const faroLevel = NGX_TO_FARO_LEVEL[level];
      if (!faroLevel) return; // TRACE and DEBUG are not mapped — skip
      const msg = typeof message === 'string' ? message : JSON.stringify(message);
      const context = additional.length
        ? { additional: additional.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ') }
        : undefined;
      pushLogToFaro(msg, faroLevel, context);
    }
  }

  private extractError(message: unknown, additional: unknown[]): Error {
    if (additional[0] instanceof Error) {
      return additional[0];
    }
    if (message instanceof Error) {
      return message;
    }
    return new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
}

export const faroLoggerMonitor = new FaroLoggerMonitor();
